import { addDays, format, parseISO, isValid, parse } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { DATE_DISPLAY_FORMAT, DATETIME_DISPLAY_FORMAT, DATETIME_DISPLAY_FORMAT_WITH_SECONDS } from '@/shared/constants/form-messages'

export type DateRangeType = 'today' | 'this-week' | 'this-month' | 'this-year' | 'custom'

export interface DateRangeResult {
  fromDate: string
  toDate: string
}

/**
 * Get today's date in YYYY-MM-DD format
 */
export const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0]
}

/**
 * Get start of month date in YYYY-MM-DD format
 */
export const getStartOfMonth = (): string => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  return startOfMonth.toISOString().split('T')[0]
}

/**
 * Format a Date object to YYYY-MM-DD string format
 */
export const formatDateToISO = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

/**
 * Calculate date range based on the specified range type
 */
export const calculateDateRange = (dateRange: DateRangeType, currentFilters?: { fromDate?: string; toDate?: string }): DateRangeResult => {
  const today = new Date()

  switch (dateRange) {
    case 'today': {
      const formattedDate = formatDateToISO(today)
      return {
        fromDate: formattedDate,
        toDate: formattedDate
      }
    }
    case 'this-week': {
      const startOfWeek = new Date(today)
      const day = today.getDay()
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
      startOfWeek.setDate(diff)
      return {
        fromDate: formatDateToISO(startOfWeek),
        toDate: formatDateToISO(today)
      }
    }
    case 'this-month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      return {
        fromDate: formatDateToISO(startOfMonth),
        toDate: formatDateToISO(today)
      }
    }
    case 'this-year': {
      const startOfYear = new Date(today.getFullYear(), 0, 1)
      return {
        fromDate: formatDateToISO(startOfYear),
        toDate: formatDateToISO(today)
      }
    }
    case 'custom': {
      // For custom, use existing filter dates or default to current month
      return {
        fromDate: currentFilters?.fromDate || getStartOfMonth(),
        toDate: currentFilters?.toDate || getTodayDate()
      }
    }
    default: {
      return {
        fromDate: currentFilters?.fromDate || getStartOfMonth(),
        toDate: currentFilters?.toDate || getTodayDate()
      }
    }
  }
}

/**
 * Create default date range state for calendar picker
 */
export const createDefaultDateRange = (): DateRange => ({
  from: addDays(new Date(), -30),
  to: new Date(),
})

/**
 * Convert DateRange to filter format
 */
export const dateRangeToFilterFormat = (selectedDateRange: DateRange | undefined): { fromDate?: string; toDate?: string } => {
  if (selectedDateRange?.from && selectedDateRange?.to) {
    return {
      fromDate: formatDateToISO(selectedDateRange.from),
      toDate: formatDateToISO(selectedDateRange.to)
    }
  }
  
  if (selectedDateRange?.from) {
    const fromDate = formatDateToISO(selectedDateRange.from)
    return {
      fromDate,
      toDate: fromDate // Set same date if only one date selected
    }
  }
  
  return {}
}

/**
 * Convert filter dates to DateRange format
 */
export const filterDatesToDateRange = (fromDate?: string, toDate?: string): DateRange | undefined => {
  if (fromDate && toDate) {
    return {
      from: new Date(fromDate),
      to: new Date(toDate)
    }
  }
  return undefined
}

export const normalizeDateString = (dateStr: string): string => {
  if (!dateStr) return ''

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }

  try {
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateStr)) {
      const date = parse(dateStr, 'yyyy/MM/dd', new Date())
      if (isValid(date)) return format(date, 'yyyy-MM-dd')
    }
    const date = parse(dateStr, 'dd/MM/yyyy', new Date())
    if (isValid(date)) {
      return format(date, 'yyyy-MM-dd')
    }
  } catch {
    return dateStr
  }

  return dateStr
}

/**
 * Format freeform input into a DD/MM/YYYY string (masked).
 * - Keeps only digits
 * - Inserts slashes as the user types
 * - Caps length to 8 digits (DDMMYYYY) -> 10 chars with slashes
 */
export const formatDdMmYyyyInput = (input: string): string => {
  const digits = (input || '').replace(/[^0-9]/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

/**
 * Validate a date string that may be either:
 * - dd/MM/yyyy (user input)
 * - yyyy-MM-dd (native date input / API date-only)
 */
export const isValidDateOnlyString = (value: string): boolean => {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return false

  const normalized = normalizeDateString(trimmed)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return false

  const date = new Date(normalized)
  return !isNaN(date.getTime())
}

export const convertDateFromISO = (isoDate?: string | null): string => {
  if (!isoDate) return ''
  try {
    const date = parseISO(isoDate)
    if (!isValid(date)) return ''
    return format(date, 'yyyy-MM-dd')
  } catch (error) {
    return ''
  }
}

/** Format ISO/date string to YYYY/MM/DD for display (single helper for all date including DOB).
 *
 * When the input is a full UTC datetime (contains 'T'), the UTC date portion is extracted
 * directly before parsing to prevent the displayed date from shifting due to local timezone
 * offset (e.g. a UTC+7 client seeing `2026-07-22T18:30:00Z` as `2026/07/23`).
 * Date-only strings (e.g. `"2026-07-22"`) are left unchanged since they have no time component.
 */
export const formatDateToDisplay = (isoDate?: string | null, fallback: string = ''): string => {
  if (!isoDate) return fallback
  try {
    const normalizedDate = isoDate.includes('T') ? isoDate.slice(0, 10) : isoDate
    const date = parseISO(normalizedDate)
    if (!isValid(date)) return fallback
    return format(date, DATE_DISPLAY_FORMAT)
  } catch {
    return fallback
  }
}

/** Format ISO datetime string to YYYY/MM/DD HH:mm or HH:mm:ss for display. */
/** Audit log style: local timezone, e.g. "Mar 19, 2025, 12:29 PM" */
export const formatAuditLogTimestamp = (isoDate?: string | null, fallback: string = '—'): string => {
  if (!isoDate) return fallback
  try {
    const date = parseISO(isoDate)
    if (!isValid(date)) return fallback
    return format(date, 'MMM d, yyyy, h:mm a')
  } catch {
    return fallback
  }
}

export const formatDateTimeToDisplay = (
  isoDate?: string | null,
  fallback: string = '',
  options?: { withSeconds?: boolean }
): string => {
  if (!isoDate) return fallback
  try {
    const date = parseISO(isoDate)
    if (!isValid(date)) return fallback
    const pattern = options?.withSeconds ? DATETIME_DISPLAY_FORMAT_WITH_SECONDS : DATETIME_DISPLAY_FORMAT
    return format(date, pattern)
  } catch {
    return fallback
  }
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const toDateOnly = (raw: string): string => {
  const s = (raw ?? '').trim()
  if (!s) return ''
  if (s.includes('T')) {
    const datePart = s.slice(0, 10)
    if (DATE_ONLY_REGEX.test(datePart)) return datePart
  }
  if (DATE_ONLY_REGEX.test(s)) return s
  const normalized = normalizeDateString(s)
  if (DATE_ONLY_REGEX.test(normalized)) return normalized
  try {
    const d = parseISO(s)
    return isValid(d) ? format(d, 'yyyy-MM-dd') : ''
  } catch {
    return normalized || ''
  }
}

export const dateOnlyToFlatpickrISO = (dateOnly: string): string | undefined => {
  const s = dateOnly.trim()
  if (!DATE_ONLY_REGEX.test(s)) return undefined
  const d = parse(s, 'yyyy-MM-dd', new Date())
  if (!isValid(d)) return undefined
  return format(d, "yyyy-MM-dd'T'HH:mm:ss")
} 
